import { Location } from '@angular/common';
import { APP_INITIALIZER, Provider } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { skipWhile, take, delay } from 'rxjs/operators';
import { StorageService } from '../global/storage.service';
import { GlobalService } from '../global/global.service';
import { HttpActionState } from '../main/pages/users/userslisting/store/users-listing.reducers';
import * as AuthActions from '../store/actions/auth.action';
import * as UserActions from '../store/actions/user.actions';
import * as AuthSelectors from './../store/selectors/auth.selectors';
import { AuthHelperService } from './helpers/auth-helper.service';

/**
 * Constants for redirect handling
 */
const REMINDER_QUERY_PARAM = 'reminder';
const CONTRACT_CONTROL_PATH = 'contract-control';

/**
 * Determines if a URL is an internal SPA route
 * @param cleanUrl - The URL to check
 * @returns true if the URL is internal to this application
 */
function isInternalAppRoute(cleanUrl: string): boolean {
    if (!cleanUrl) return false;
    try {
        const parsed = new URL(cleanUrl, window.location.origin);
        return parsed.origin === window.location.origin;
    } catch {
        // Relative path handling e.g. '/path', '/#path' or '#/path' => treat as internal
        return cleanUrl.startsWith('/') || cleanUrl.startsWith('#') || cleanUrl.startsWith('/#');
    }
}

/**
 * Fallback navigation using window.location
 * @param url - The URL to navigate to
 */
function fallbackToWindow(url: string): void {
    try {
        // If url already starts with '#', avoid double '#'
        const urlFragment = url.startsWith('#') ? url : '/#' + url;
        window.location.href = window.location.origin + urlFragment;
    } catch (err) {
        console.warn('Fallback navigation failed:', err);
    }
}

export const UserStateProvider: Provider = {
    provide: APP_INITIALIZER,
    multi: true,
    deps: [Store, Location, AuthHelperService, Router, StorageService, GlobalService],
    useFactory: (store: Store, location: Location, authHelperService: AuthHelperService, router: Router, storageService: StorageService, globalService: GlobalService) => () => {
        const token = authHelperService.getLocalDevToken();
        if (token) {
            store.dispatch(AuthActions.markAsLoogedIn());
        }

        return new Promise((resolve) => {
            
            const currentUrl = location.path();
            
            const restrictedUrls = [
                'social-callback',
                'download/',
                's/',
                'online/',
                'email-verify/',
                'reset-password-v3/',
                'switchuser',
                'confirmInvitation',
                'iframeable',
                'shareable/',
                'trail-period-completed',
                'onboarding/',
                'export-pdf-view/',
                'crm-login',
                'login',
                'register',
                'forgot-password-v2',
                'forgot-password',
            ];

            const isRestrictedUrl = restrictedUrls.some(prefix => currentUrl.startsWith(`/${prefix}`));

            // Only save redirect URL if user is NOT logged in AND URL contains reminder query param
            // Check for token to determine if user is authenticated
            const hasToken = authHelperService.getLocalDevToken();
            let shouldSaveRedirect = false;
            
            if (!isRestrictedUrl && currentUrl && currentUrl !== '/' && !hasToken) {
                try {
                    const parsed = new URL(currentUrl, window.location.origin);
                    shouldSaveRedirect = parsed.searchParams.get(REMINDER_QUERY_PARAM) === 'true';
                } catch {
                    // Fallback to string check if URL parsing fails
                    shouldSaveRedirect = currentUrl.includes('reminder=true');
                }
                
                if (shouldSaveRedirect) {
                    storageService.addItem('redirectAfterLogin', currentUrl);
                }
            }

            if (isRestrictedUrl) {
                resolve(true);
                return;
            } else {
                store.dispatch(AuthActions.fetchUserData());
                store.dispatch(AuthActions.hubspotTrackingData());
                store.select(AuthSelectors.loadingUserDataStatus)
                    .pipe(
                        skipWhile(x => x === HttpActionState.Init || x === HttpActionState.Busy),
                        take(1)
                    )
                    .subscribe(() => {
                        // Dispatch user actions first
                        store.dispatch(new UserActions.START_USER_INACTIVITY_DETECTION());
                        store.dispatch(AuthActions.scheduleRefreshSession());
                        
                        // Handle redirect after login using RxJS delay for deterministic timing
                        const redirectUrl = decodeURIComponent(storageService.getItem('redirectAfterLogin') || '');
                        
                        if (redirectUrl && redirectUrl !== '/' && redirectUrl !== 'null' && redirectUrl !== 'undefined') {
                            // Use RxJS delay to ensure navigation happens after current execution context
                            store.select(AuthSelectors.loadingUserDataStatus)
                                .pipe(
                                    take(1),
                                    delay(0) // Delay to next tick to ensure all synchronous operations complete
                                )
                                .subscribe(() => {
                                    try {
                                        const cleanUrl = redirectUrl.replace(/^\"|\"$/g, '');
                                        
                                        // Only navigate if URL contains contract-control path
                                        if (cleanUrl.includes(CONTRACT_CONTROL_PATH)) {
                                            if (isInternalAppRoute(cleanUrl)) {
                                                // Use Angular router for proper SPA navigation
                                                router.navigateByUrl(cleanUrl).then(success => {
                                                    if (success) {
                                                        // Navigation successful, clear the storage
                                                        storageService.removeItem('redirectAfterLogin');
                                                    } else {
                                                        // Navigation failed, fallback to window.location
                                                        console.warn('Angular router navigation failed, using fallback');
                                                        fallbackToWindow(cleanUrl);
                                                    }
                                                }).catch(error => {
                                                    console.warn('Navigation error:', error);
                                                    // Fallback to window.location on error
                                                    fallbackToWindow(cleanUrl);
                                                });
                                            } else {
                                                // Not an internal route - use fallback
                                                fallbackToWindow(cleanUrl);
                                            }
                                        } else {
                                            // URL doesn't contain contract-control, just clear storage
                                            storageService.removeItem('redirectAfterLogin');
                                        }
                                        
                                    } catch (error) {
                                        console.warn('Redirect error:', error);
                                        globalService.toastrError('Unable to navigate to the requested page. If you were trying to access a contract, please re-open the link from your email.');
                                        storageService.removeItem('redirectAfterLogin');
                                    }
                                });
                        } else {
                            // Clear the storage if there's no valid URL
                            if (storageService.getItem('redirectAfterLogin')) {
                                storageService.removeItem('redirectAfterLogin');
                            }
                        }

                        resolve(true);
                    });
            }


        })
    }
}
